import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { NumericTextBoxModule } from '@progress/kendo-angular-inputs';
import { CardHeaderComponent, CardModule } from '@progress/kendo-angular-layout';
import { cartIcon, infoSolidIcon } from '@progress/kendo-svg-icons';
import { Subscription } from 'rxjs';
import { BootstrapBreakpoints } from 'src/app/shared/constants/bootstrap-breakpoints';
import { ProductWithInventoryAndQuantity } from 'src/app/shared/models/order-creation/product-with-quantity';
import { PimProductsService } from 'src/app/shared/services/pimproducts/pimproducts.service';
import { Utils } from '../../utils';
import { LoadingPanelComponent } from '../loading-panel';
import { ColumnSettings } from '../../models/ui/column-settings';

@Component({
  selector: 'ntw-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss', '../../styles/button.scss'],
  imports: [
    ButtonModule,
    CardHeaderComponent,
    CardModule,
    CommonModule,
    FormsModule,
    LoadingPanelComponent,
    NumericTextBoxModule,
    SVGIconModule,
    TranslateModule,
  ]
})
export class ProductCardComponent implements OnInit, OnDestroy {
  @Input() product: ProductWithInventoryAndQuantity;
  @Input() columnSettings?: ColumnSettings[];
  @Output() addToCart = new EventEmitter<ProductWithInventoryAndQuantity>();
  @Output() viewDetails = new EventEmitter<ProductWithInventoryAndQuantity>();
  private pimSubscription?: Subscription;

  loading = true;
  imageLoading = true;
  imageUrl: string;
  imageOpacity = 1;
  cardWidth = '280px';
  quantity = 1;

  icons = {
    cart: cartIcon,
    info: infoSolidIcon
  };

  constructor(
    private breakpointObserver: BreakpointObserver,
    private pimProductsService: PimProductsService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.setupBreakpointObserver();
    this.fetchImageUrl();
  }

  ngOnDestroy(): void {
    this.pimSubscription?.unsubscribe();
  }

  /**
   * Gets filtered column settings excluding always visible 'Name' and 'Quantity' fields.
   * The excluded fields ('Name' and 'Quantity') are always visible and rendered differently in the card view.
   * @returns Filtered column settings array.
   */
  public get filteredColumnSettings(): ColumnSettings[] {
    return this.columnSettings?.filter(column => {
      return column.field !== 'Name' && column.field !== 'Quantity';
    }) ?? [];
  }

  private setupBreakpointObserver() {
    this.breakpointObserver.observe([
      BootstrapBreakpoints.xxs,
      BootstrapBreakpoints.xs,
      BootstrapBreakpoints.sm,
      BootstrapBreakpoints.md,
      BootstrapBreakpoints.lg,
      BootstrapBreakpoints.xl,
      BootstrapBreakpoints.xxl
    ]).subscribe(result => {
      if (result.matches) {
        if (result.breakpoints[BootstrapBreakpoints.xxs]) {
          this.cardWidth = '180px';
        }
        else if (result.breakpoints[BootstrapBreakpoints.xs]) {
          this.cardWidth = '200px';
        }
        else if (result.breakpoints[BootstrapBreakpoints.sm]) {
          this.cardWidth = '230px';
        }
        else if (result.breakpoints[BootstrapBreakpoints.md]) {
          this.cardWidth = '230px';
        }
        else if (result.breakpoints[BootstrapBreakpoints.lg]) {
          this.cardWidth = '280px';
        }
        else if (result.breakpoints[BootstrapBreakpoints.xl]) {
          this.cardWidth = '250px';
        }
        else if (result.breakpoints[BootstrapBreakpoints.xxl]) {
          this.cardWidth = '280px';
        }
      }
    });
  }

  public formatNumberField(value: number | undefined, fractionDigits: number): string {
    return value ? Utils.FormatNumber(value, fractionDigits) : this.translateService.instant('shared.unknown');
  }

  private fetchImageUrl(): void {
    this.pimSubscription = this.pimProductsService.get(this.product.AXCode).subscribe({
      next: (pimProduct) => {
        if (!pimProduct) {
          this.loading = false;
          return;
        }
        const assets = pimProduct.mediaRelation.sort((a, b) => parseInt(b.fileId) - parseInt(a.fileId)); // sort by fileId descending
        this.imageUrl = pimProduct.mainImageURL ?? assets.find(asset => asset.type === 'Can Image')?.staticUrl;
        this.loading = false;
      },
      error: error => {
        console.error('Error while getting PIM product', error);
        this.loading = false;
      }
    });
  }

  public onImageLoad(): void {
    this.imageLoading = false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onImageError(event: any) {
    this.imageOpacity = 0.2;
    event.target.src = 'assets/images/noimage.png';
  }

  public onAddToCart(): void {
    this.addToCart.emit({ ...this.product, Quantity: this.quantity });
  }

  public onViewDetails(): void {
    this.viewDetails.emit(this.product);
  }
}
